import { lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import { HashRouter, Navigate, Route, Routes } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { AppLoading, PanelLoading } from "@/components/layout/AppLoading";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider } from "@/features/auth/AuthProvider";
import { ProtectedRoute } from "@/features/auth/ProtectedRoute";
import { RequireAuth } from "@/features/auth/RequireAuth";
import { selectPermissions } from "@/features/auth/authSlice";
import { getLandingPath, PERMISSIONS } from "@/features/auth/permissions";
import { InvoicesPage } from "@/pages/InvoicesPage";
import { ApprovalRequestsPage } from "@/pages/ApprovalRequestsPage";
import { ApprovalRulesPage } from "@/pages/ApprovalRulesPage";
import { InvoiceDetailPage } from "@/pages/InvoiceDetailPage";

const LoginPage = lazy(() => import("@/pages/LoginPage"));
const NoPermissionPage = lazy(() => import("@/pages/NoPermissionPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const LogsPage = lazy(() => import("@/pages/LogsPage"));

/** Sends the user to the first queue their permissions allow. */
function LandingRedirect() {
  const permissions = useSelector(selectPermissions);
  return <Navigate to={getLandingPath(permissions)} replace />;
}

function guarded(permission, element) {
  return <ProtectedRoute requiredPermission={permission}>{element}</ProtectedRoute>;
}

/**
 * Every invoice queue is the same component. Each gets its own `key`, so moving
 * between queues remounts it — filters, search, selection and page number reset
 * on their own, with no effect watching the route.
 */
function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <Suspense fallback={<AppLoading label="Signing you in" />}>
            <LoginPage />
          </Suspense>
        }
      />
      <Route
        path="/no-permission"
        element={
          <Suspense fallback={<AppLoading />}>
            <NoPermissionPage />
          </Suspense>
        }
      />

      <Route element={<RequireAuth />}>
        <Route path="/" element={<AppLayout />}>
          <Route index element={<LandingRedirect />} />

          <Route
            path="my-invoices"
            element={guarded(PERMISSIONS.MY_INVOICES, <InvoicesPage key="my-invoices" variant="my-invoices" />)}
          />
          <Route
            path="invoice-manager"
            element={guarded(PERMISSIONS.ALL_INVOICES, <InvoicesPage key="invoice-manager" variant="invoice-manager" />)}
          />
          <Route
            path="inQuery-invoices"
            element={guarded(PERMISSIONS.ALL_INVOICES, <InvoicesPage key="inQuery-invoices" variant="inQuery-invoices" />)}
          />
          <Route
            path="failed-invoices"
            element={guarded(PERMISSIONS.ALL_INVOICES, <InvoicesPage key="failed-invoices" variant="failed-invoices" />)}
          />
          <Route
            path="starred-invoices"
            element={<InvoicesPage key="starred-invoices" variant="starred-invoices" />}
          />

          <Route
            path="invoice/view/:id"
            element={guarded(PERMISSIONS.INVOICE_DETAILS, <InvoiceDetailPage viewOnly />)}
          />
          <Route
            path="invoice/edit/:id"
            element={guarded(PERMISSIONS.UPDATE_INVOICE, <InvoiceDetailPage />)}
          />

          <Route
            path="approval-requests"
            element={guarded(PERMISSIONS.APPROVAL_REQUESTS, <ApprovalRequestsPage />)}
          />
          <Route
            path="approval-rules"
            element={guarded(PERMISSIONS.APPROVAL_RULES, <ApprovalRulesPage />)}
          />

          <Route
            path="logs"
            element={guarded(
              PERMISSIONS.SHOW_INVOICE_CREATION_LOGS,
              <Suspense fallback={<PanelLoading />}>
                <LogsPage />
              </Suspense>,
            )}
          />

          <Route
            path="settings"
            element={
              <Suspense fallback={<PanelLoading />}>
                <SettingsPage />
              </Suspense>
            }
          />

          <Route
            path="not-found"
            element={
              <Suspense fallback={<PanelLoading />}>
                <NotFoundPage />
              </Suspense>
            }
          />
          <Route path="*" element={<Navigate to="/not-found" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <HashRouter>
      <ErrorBoundary>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ErrorBoundary>
      <Toaster />
    </HashRouter>
  );
}
